import React, { useMemo } from 'react'
import { convertUnitAndValue } from '../../../utils/utils'
import { UNITS } from '../../../types/misc.types'
import { GROUPED_STATIC_TEXT_ROLE } from '../../ui-components/NameValueTableRow'

type RowData = {
  name: string
  weightage: number
}

interface HoldingsProps {
  data?: RowData[]
  color?: string
  isShort?: boolean
  /** Accessible list name, e.g. "Holdings" or "Sectors" */
  listLabel?: string
}

const Holdings = ({ data = [], color = '#E6F2FF', isShort = true, listLabel = 'Holdings' }: HoldingsProps) => {
  const MAX_WIDTH_ALLOWED = 85
  const MIN_WIDTH_ALLOWED = 1
  const normalizedData = useMemo(() => {
    if (data.length === 0) return []
    const minPercentage = Math.min(...data.map((row) => row.weightage))
    const maxPercentage = Math.max(...data.map((row) => row.weightage))

    return data.map((row) => {
      const normalizedPercentage =
        1 +
        ((row.weightage - minPercentage) / (maxPercentage - minPercentage)) * (MAX_WIDTH_ALLOWED - MIN_WIDTH_ALLOWED)
      const formattedWeight = convertUnitAndValue({
        value: row?.weightage ?? 0,
        unit: UNITS.PERCENTAGE
      })
      return { ...row, normalizedPercentage, formattedWeight, accessibleLabel: `${row.name}, ${formattedWeight}` }
    })
  }, [data])

  const visibleRows = isShort ? normalizedData.slice(0, 5) : normalizedData

  return (
    <ul role='list' aria-label={listLabel} className='list-none m-0 p-0 flex flex-col'>
      {visibleRows.map((row, index) => (
        <li key={`${row.name}-${index}`} role='listitem' className='list-none m-0 p-0'>
          <div
            role={GROUPED_STATIC_TEXT_ROLE}
            aria-label={row.accessibleLabel}
            className='relative h-10 border-b border-[#FFFFFF] text-13 z-0'
          >
            <div aria-hidden='true' className='absolute top-0 flex w-full h-full justify-between items-center px-4 text-black z-[1]'>
              <div className='font-normal text-[#004D99]'>{row.name}</div>
              <div className='font-medium'>{row.formattedWeight} </div>
            </div>
            <div
              aria-hidden='true'
              className='absolute top-0 left-0 h-full'
              style={{
                width: `${row.normalizedPercentage}%`,
                backgroundColor: color,
                opacity: 1
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}

export default Holdings
